import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatHm } from './types';

interface Props {
  open: boolean;
  workdayId: string | null;
  minutes: number;
  onClose: () => void;
}

/**
 * Modal post-fichaje-OUT para decidir qué hacer con las horas a compensar
 * del día. Aceptar requiere notas obligatorias (justificación). Rechazar
 * descarta las horas del saldo a compensar sin tocar los punches — el
 * balance de la jornada sigue mostrándose en el resumen para trazabilidad.
 *
 * Si el usuario cierra el modal sin decidir, la jornada queda en estado
 * PENDING y aparece luego en la pestaña "Horas a compensar" de /solicitudes.
 */
export function OvertimeApprovalModal({ open, workdayId, minutes, onClose }: Props) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [mode, setMode] = useState<'idle' | 'accepting'>('idle');

  function closeNow() {
    setNotes('');
    setMode('idle');
    approveMutation.reset();
    rejectMutation.reset();
    onClose();
  }

  const approveMutation = useMutation({
    mutationFn: () =>
      api(`/overtime/workday/${workdayId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ notes: notes.trim() }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overtime'] });
      queryClient.invalidateQueries({ queryKey: ['punch', 'today'] });
      closeNow();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      api(`/overtime/workday/${workdayId}/reject`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overtime'] });
      queryClient.invalidateQueries({ queryKey: ['punch', 'today'] });
      closeNow();
    },
  });

  const pending = approveMutation.isPending || rejectMutation.isPending;
  const error = approveMutation.error ?? rejectMutation.error;
  const errorMessage = error
    ? error instanceof ApiError
      ? error.message
      : 'Error al guardar la decisión'
    : null;

  // Cierre disparado por el usuario (X, backdrop, "Atrás"): bloqueado
  // mientras hay una mutación en curso para que no pierda la solicitud
  // a medio vuelo. El cierre programático tras onSuccess no pasa por
  // aquí — usa closeNow() directamente.
  function handleUserClose() {
    if (pending) return;
    closeNow();
  }

  function handleAccept() {
    if (mode !== 'accepting') {
      setMode('accepting');
      return;
    }
    if (notes.trim().length === 0) return;
    if (approveMutation.isPending) return;
    approveMutation.mutate();
  }

  if (!open || !workdayId) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        aria-hidden
        onClick={handleUserClose}
        className="absolute inset-0"
        style={{ background: 'rgba(10,14,31,0.4)', backdropFilter: 'blur(2px)' }}
      />
      <div className="relative w-full max-w-[480px] bg-card rounded-sm shadow-lg overflow-hidden">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-[15px] font-semibold tracking-tightish">Horas a compensar</h2>
          <button
            type="button"
            onClick={handleUserClose}
            aria-label="Cerrar"
            disabled={pending}
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-line-strong bg-card text-foreground hover:border-foreground transition-colors disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <div className="px-6 py-5 flex flex-col gap-4">
          <p className="text-[13px] text-muted-foreground">
            Hoy te has quedado más allá de tu jornada.
          </p>
          <div className="flex items-baseline justify-between gap-3 px-4 py-3 rounded-sm border bg-icaria-100 border-icaria-200">
            <span className="text-[12px] font-medium uppercase tracking-cap text-icaria-700">
              Tiempo a compensar
            </span>
            <span className="font-mono text-[22px] font-semibold tabular-nums text-icaria-700">
              {formatHm(minutes)}
            </span>
          </div>

          {mode === 'accepting' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ot-approve-notes">
                Motivo <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="ot-approve-notes"
                rows={3}
                maxLength={500}
                placeholder="Ej. Cierre de proyecto X, reunión imprevista con cliente…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                autoFocus
              />
              <span className="text-[11px] text-muted-foreground">
                Las notas son obligatorias y quedarán visibles en tu histórico.
              </span>
            </div>
          ) : (
            <p className="text-[13px] text-foreground">
              Si aceptas, estas horas se sumarán a tu saldo a compensar (luego puedes canjearlas
              por días de descanso compensatorio). Si rechazas, no contarán.
            </p>
          )}

          {errorMessage ? (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage}
            </p>
          ) : null}
        </div>

        <footer className="flex justify-between gap-2 px-6 py-3.5 border-t border-border bg-paper">
          {mode === 'accepting' ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setMode('idle')}
              disabled={pending}
            >
              Atrás
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => rejectMutation.mutate()}
              disabled={pending}
            >
              <X className="h-3.5 w-3.5" />
              Rechazar
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleAccept}
            disabled={pending || (mode === 'accepting' && notes.trim().length === 0)}
          >
            <Check className="h-3.5 w-3.5" />
            {mode === 'accepting'
              ? approveMutation.isPending
                ? 'Guardando…'
                : 'Confirmar y aceptar'
              : 'Aceptar'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
